import axios, { AxiosResponse } from 'axios'
import { ID } from '../../../../../_metronic/helpers'
import { extraiNomeArquivo } from '../../indicadores/indicadores-painel/core/_requests'
import {
  AgrupamentoQuantitativo,
  ArquivoRelatorio,
  FormatoRelatorio,
  IndicadoresInterconsulta,
  ParametrosAnexoIi,
  ParametrosPeriodo,
  QuantitativoInterconsulta,
  RatcQueryResponse,
  RatcRegistro,
  RelatorioAnexoIi,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const RELATORIO_URL = `${API_URL}/atendimentos/interconsulta/relatorio`

/**
 * Exportações devolvem bytes com `Content-Disposition` (não `ArquivoDto`): `responseType: 'blob'`.
 * O nome de reserva cobre o caso de o header não ser exposto ao navegador.
 */
const baixar = async (
  url: string,
  params: object,
  formato: FormatoRelatorio,
  nomeReserva: string
): Promise<ArquivoRelatorio> => {
  const resposta = await axios.get(url, { params: { ...params, formato }, responseType: 'blob' })
  const contentDisposition =
    resposta.headers?.['content-disposition'] ?? resposta.headers?.['Content-Disposition']
  return {
    blob: resposta.data as Blob,
    nomeArquivo: extraiNomeArquivo(contentDisposition) ?? `${nomeReserva}.${formato}`,
  }
}

const sufixo = (p: ParametrosPeriodo) => `${p.empresa}-${p.dataInicio}-${p.dataFim}`

export const getIndicadoresInterconsulta = (
  params: ParametrosPeriodo
): Promise<IndicadoresInterconsulta> =>
  axios
    .get(`${RELATORIO_URL}/indicadores`, { params })
    .then((d: AxiosResponse<IndicadoresInterconsulta>) => d.data)

export const exportarIndicadoresInterconsulta = (params: ParametrosPeriodo) =>
  baixar(
    `${RELATORIO_URL}/indicadores`,
    params,
    'csv',
    `indicadores-interconsulta-${sufixo(params)}`
  )

export const getQuantitativoInterconsulta = (
  params: ParametrosPeriodo,
  agrupar: AgrupamentoQuantitativo
): Promise<QuantitativoInterconsulta> =>
  axios
    .get(`${RELATORIO_URL}/quantitativo`, { params: { ...params, agrupar } })
    .then((d: AxiosResponse<QuantitativoInterconsulta>) => d.data)

export const exportarQuantitativoInterconsulta = (
  params: ParametrosPeriodo,
  agrupar: AgrupamentoQuantitativo
) =>
  baixar(
    `${RELATORIO_URL}/quantitativo`,
    { ...params, agrupar },
    'csv',
    `quantitativo-interconsulta-${agrupar}-${sufixo(params)}`
  )

/** Página do RATC; `query` montada pelo QueryResponseProvider (page, size, empresa, período, ceae). */
export const getRatc = (query: string): Promise<RatcQueryResponse> =>
  axios.get(`${RELATORIO_URL}/ratc?${query}`).then((d: AxiosResponse<RatcQueryResponse>) => d.data)

export const exportarRatc = (params: ParametrosPeriodo, formato: 'json' | 'csv') =>
  baixar(`${RELATORIO_URL}/ratc/exportar`, params, formato, `ratc-${sufixo(params)}`)

/** Prévia em JSON do Anexo II: linhas + contadores de qualidade do dado. */
export const getAnexoIi = (params: ParametrosAnexoIi): Promise<RelatorioAnexoIi> =>
  axios
    .get(`${RELATORIO_URL}/anexo-ii`, { params: { ...params, formato: 'json' } })
    .then((d: AxiosResponse<RelatorioAnexoIi>) => d.data)

export const exportarAnexoIi = (params: ParametrosAnexoIi, formato: FormatoRelatorio) =>
  baixar(
    `${RELATORIO_URL}/anexo-ii`,
    params,
    formato,
    `anexo-ii-${params.empresa}-${params.competencia}`
  )

/** Registro RATC completo de uma interconsulta (com motivo e réplicas). */
export const getRatcDoAtendimento = (idAtendimento: ID): Promise<RatcRegistro> =>
  axios
    .get(`${API_URL}/atendimentos/interconsulta/${idAtendimento}/ratc`)
    .then((d: AxiosResponse<RatcRegistro>) => d.data)
